/**
 * Segments API Service
 * 
 * HTTP client for segment endpoints using centralized Axios instance.
 */

// Use the authoritative client that shares state with AuthContext
import { apiClient as api } from '../../services/api/client';
import type { Segment } from './types';

/** Standard API response type */
type ApiResponse<T> = { success: boolean; data: T; error?: string };

/**
 * Segments API
 */
export const segmentsApi = {

    /**
     * Start or continue a conversation
     */
    async converse(params: {
        brandId: string;
        segmentId?: string;
        message: string;
        filter?: Record<string, unknown>;
        count?: number;
        summary?: string;
    }): Promise<ApiResponse<any>> {
        const response = await api.post('/segments/conversation', params);
        return response.data;
    },

    /**
     * Crea un segmento "mock" sin pasar por la IA — útil para demos y
     * para entornos sin OpenAI configurado.
     *
     * También se usa desde el manual filter builder, donde mandamos el
     * filter armado por el usuario + el count estimado en el preview.
     */
    async createMock(params: {
        brandId: string;
        name: string;
        description: string;
        filter?: Record<string, unknown>;
        filteredFields?: string[];
        lastKnownCount?: number;
        emoji?: string;
    }): Promise<ApiResponse<Segment>> {
        const response = await api.post('/segments/mock', params);
        return response.data;
    },

    /**
     * List segments for brand
     */
    async list(brandId: string, status?: 'draft' | 'saved' | 'archived'): Promise<ApiResponse<Segment[]>> {
        const params = new URLSearchParams({ brandId });
        if (status) params.append('status', status);

        const response = await api.get(`/segments?${params.toString()}`);
        return response.data;
    },

    /**
     * Get segment by ID
     */
    async get(id: string, brandId: string): Promise<ApiResponse<Segment>> {
        const response = await api.get(`/segments/${id}?brandId=${brandId}`);
        return response.data;
    },

    /**
     * Save segment with name
     */
    async save(id: string, brandId: string, name: string): Promise<ApiResponse<Segment>> {
        const response = await api.post(`/segments/${id}/save`, { brandId, name });
        return response.data;
    },

    /**
     * Undo last action
     */
    async undo(id: string, brandId: string): Promise<ApiResponse<any>> {
        const response = await api.post(`/segments/${id}/undo`, { brandId });
        return response.data;
    },

    /**
     * Delete segment
     */
    async delete(id: string, brandId: string): Promise<{ success: boolean }> {
        const response = await api.delete(`/segments/${id}?brandId=${brandId}`);
        return response.data;
    },

    /**
     * Remueve un filtro específico del segmento (X en un chip).
     * Devuelve el estado actualizado (filter, count, history con turno bot).
     */
    async removeFilter(id: string, brandId: string, field: string): Promise<ApiResponse<any>> {
        const response = await api.post(`/segments/${id}/remove-filter`, {
            brandId,
            field,
        });
        return response.data;
    },

    /**
     * Duplica un segmento existente. La copia queda en estado "draft"
     * con el nombre original + " (copia)".
     */
    async duplicate(id: string, brandId: string): Promise<ApiResponse<Segment>> {
        const response = await api.post(`/segments/${id}/duplicate`, { brandId });
        return response.data;
    },
};

export default segmentsApi;
